import { useParams } from "#routers/hooks";
import { enhanceSource } from "#utils/recordUtils";
import { UseQueryOptions, useQueries, useQuery } from "@tanstack/react-query";
import type { RecordType, UserReturnType } from "@validereinc/domain";
import {
  EstimationMethodAdapter,
  RecordDomain,
  RecordValueConfigurationSourceType,
  RecordValueConfigurationTypeType,
  UsersAdapter,
} from "@validereinc/domain";
import React, { createContext, useContext, useMemo } from "react";

export const RecordContext = createContext<{
  record?: RecordType;
  users?: UserReturnType[];
  isLoading: boolean;
  refetch: () => void;
} | null>(null);

export const RecordProvider = ({
  children,
}: {
  children: React.ReactElement | React.ReactElement[];
}) => {
  const parentContext = useContext(RecordContext);
  const { recordId } = useParams<{ recordId: string }>();

  const recordQuery = useQuery({
    queryKey: ["record", recordId],
    queryFn: () => {
      return RecordDomain.getOne({ recordId });
    },
    enabled: !!recordId,
  });
  const metaFieldUsersQueries = useQueries<
    Array<
      UseQueryOptions<
        Awaited<ReturnType<typeof UsersAdapter.getOne>> | undefined,
        unknown,
        UserReturnType | undefined
      >
    >
  >({
    queries: recordQuery.data
      ? Array.from(
          new Set([
            recordQuery.data.updated_by,
            ...(recordQuery.data.values.map((d) => d.updated_by) ?? []),
          ])
        ).map((id) => ({
          queryKey: ["users", id],
          queryFn: () => {
            if (!id) {
              return;
            }

            return UsersAdapter.getOne({ id });
          },
          enabled: Boolean(id),
          staleTime: 3 * 60 * 1000,
          select: (resp) => resp?.data,
        }))
      : [],
  });

  const refetch = () => {
    recordQuery.refetch();
  };

  /** TODO: Remove these lookups when the backend can provide the entity type as well */
  const estimationMethodParams = {
    filters: {
      year_month: recordQuery.data?.year_month
        ? [recordQuery.data?.year_month]
        : [],
      id: recordQuery.data?.values
        .filter(
          ({ configuration: { configuration_type } }) =>
            configuration_type ===
            RecordValueConfigurationTypeType.CALCULATION_RESULT
        )
        .flatMap(({ configuration: { sources } }) => sources)
        .map(
          ({ estimation_method_id }: RecordValueConfigurationSourceType) =>
            estimation_method_id
        ),
    },
  };

  const estimationMethodQuery = useQuery({
    queryKey: ["estimationMethodConfigurations", estimationMethodParams],
    queryFn: () =>
      // @ts-expect-error TODO: Typing on filter
      EstimationMethodAdapter.configuration.getList(estimationMethodParams),
    enabled: !!estimationMethodParams.filters.id?.length,
    select: ({ data }) => data,
  });

  const recordWithSources = useMemo(
    () =>
      recordQuery.data
        ? {
            ...recordQuery.data,
            values: recordQuery.data?.values?.map((value) => ({
              ...value,
              configuration: {
                ...value.configuration,
                sources: value.configuration.sources?.map(
                  (source: RecordValueConfigurationSourceType) =>
                    enhanceSource(source, estimationMethodQuery.data ?? [])
                ),
              },
            })),
          }
        : undefined,
    [recordQuery.data, estimationMethodQuery.data]
  );
  /** TODO: Remove these lookups when the backend can provide the entity type as well */

  return (
    <RecordContext.Provider
      value={
        parentContext ?? {
          record: recordWithSources,
          users: metaFieldUsersQueries.reduce<UserReturnType[]>((list, q) => {
            if (!q.data) {
              return list;
            }

            list.push(q.data);
            return list;
          }, []),
          isLoading:
            recordQuery.isLoading ||
            (!!estimationMethodParams.filters.id?.length &&
              estimationMethodQuery.isLoading),
          refetch,
        }
      }
    >
      {children}
    </RecordContext.Provider>
  );
};
